/** Labels and field lists for discharge / transfer summaries (Phase 16E). */
import type {
  DeliveryMethod,
  DeliveryRecipientRole,
  DischargeSummaryDueState,
  DischargeSummaryKind,
  DischargeSummaryStatus,
  GoalOutcome,
  NarrativeField,
} from '../../../dtos/patientmr/dischargeSummary.dto';

export const NARRATIVE_FIELDS: Record<DischargeSummaryKind, { field: NarrativeField; label: string; rows?: number }[]> = {
  DISCHARGE: [
    { field: 'courseOfCare', label: 'Course of care', rows: 4 },
    { field: 'conditionAtDischarge', label: 'Condition at discharge', rows: 3 },
    { field: 'instructionsGivenTo', label: 'Discharge instructions given to', rows: 1 },
    { field: 'instructions', label: 'Discharge instructions', rows: 3 },
    { field: 'equipmentDisposition', label: 'Equipment (DME) returned or kept', rows: 2 },
    { field: 'followUp', label: 'Follow-up', rows: 2 },
    { field: 'referrals', label: 'Referrals', rows: 2 },
    { field: 'receivingProvider', label: 'Receiving provider', rows: 1 },
  ],
  TRANSFER: [
    { field: 'transferReason', label: 'Reason for transfer', rows: 2 },
    { field: 'receivingFacility', label: 'Receiving facility', rows: 1 },
    { field: 'conditionAtTransfer', label: 'Condition at transfer', rows: 3 },
    { field: 'currentOrdersSummary', label: 'Current orders', rows: 3 },
    { field: 'narrative', label: 'Narrative', rows: 3 },
  ],
};

export const KIND_LABEL: Record<DischargeSummaryKind, string> = {
  DISCHARGE: 'Discharge summary',
  TRANSFER: 'Transfer summary',
};

export const STATUS_LABEL: Record<DischargeSummaryStatus, string> = {
  DRAFT: 'Draft',
  IN_QA: 'In QA review',
  SIGNED: 'Signed',
  CLOSED_NOT_REQUIRED: 'Closed — not required',
};

export const DUE_LABEL: Record<DischargeSummaryDueState, string> = {
  DUE: 'Due',
  DUE_TODAY: 'Due today',
  OVERDUE: 'Overdue',
  SENT: 'Sent',
  NOT_REQUIRED: 'Not required',
};

export const DUE_STYLE: Record<DischargeSummaryDueState, string> = {
  DUE: 'bg-sky-50 text-sky-900 border-sky-200',
  DUE_TODAY: 'bg-amber-50 text-amber-900 border-amber-300',
  OVERDUE: 'bg-red-50 text-red-800 border-red-300',
  SENT: 'bg-emerald-50 text-emerald-800 border-emerald-200',
  NOT_REQUIRED: 'bg-slate-50 text-slate-700 border-slate-200',
};

export const GOAL_OUTCOME_OPTIONS: { value: GoalOutcome; label: string }[] = [
  { value: 'MET', label: 'Met' },
  { value: 'NOT_MET', label: 'Not met' },
  { value: 'UNRESOLVED', label: 'Unresolved' },
];

export const RECIPIENT_ROLE_OPTIONS: { value: DeliveryRecipientRole; label: string }[] = [
  { value: 'PCP', label: 'Primary care practitioner' },
  { value: 'RECEIVING_AGENCY', label: 'Receiving agency' },
  { value: 'FACILITY', label: 'Hospital or facility' },
  { value: 'PAYER', label: 'Health plan' },
  { value: 'OTHER', label: 'Other' },
];

export const DELIVERY_METHOD_OPTIONS: { value: DeliveryMethod; label: string }[] = [
  { value: 'FAX', label: 'Fax' },
  { value: 'MAIL', label: 'Mail' },
  { value: 'HAND', label: 'Hand-delivered' },
  { value: 'PORTAL', label: 'Provider or payer portal' },
  { value: 'SECURE_EMAIL', label: 'Secure email' },
  { value: 'OTHER', label: 'Other' },
];

export const labelOf = <T extends string>(options: { value: T; label: string }[], value: T): string =>
  options.find((o) => o.value === value)?.label ?? value;

/** MM/DD/YYYY from 'YYYY-MM-DD…' (date part only, no timezone shift); '—' when missing. */
export function formatUsDate(value: string | null | undefined): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(value ?? '');
  return m ? `${m[2]}/${m[3]}/${m[1]}` : '—';
}
